import { Component, OnInit, OnDestroy } from "@angular/core";
import { Router, ActivatedRoute } from "@angular/router";
import { packageTrackService } from "./package-track.service";
import { NgxSpinnerService } from "ngx-spinner";
import { ToastrService } from "ngx-toastr";
import { WebSocketService } from "../web-socket.service";
import { Subscription } from "rxjs";

declare var google: any;

@Component({
  selector: "app-package-track",
  templateUrl: "./package-track.component.html",
  styleUrls: ["./package-track.component.sass"],
})
export class PackageTrackComponent implements OnInit, OnDestroy {
  greenIcon = "../../assets/images/greenIcon.png";
  vehicleIcon = "../../assets/images/vehicleIcon.png";
  redIcon = "../../assets/images/redIcon.png";

  id: any;
  packageId: any;
  status: number = 0;

  socketSubscription!: Subscription;

  origin = {
    lat: 0,
    lng: 0,
  };

  destination = {
    lat: 0,
    lng: 0,
  };

  driverLocation = {
    lat: 0,
    lng: 0,
  };

  mapCenter = {
    lat: 0,
    lng: 0,
  };

  zoom = 13;

  googleMap: any;
  directionsService: any;
  directionsRenderer: any;

  lastRouteOrigin = {
    lat: 0,
    lng: 0,
  };

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private packageTrackservice: packageTrackService,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService,
    private websocketService: WebSocketService,
  ) {}

  ngOnInit(): void {
    this.id = this.activatedRoute.snapshot.queryParams.id;

    this.getPackageById();
  }

  //==========================
  // Map Ready
  //==========================

  onMapReady(map: any) {
    this.googleMap = map;

    this.directionsService = new google.maps.DirectionsService();

    this.directionsRenderer = new google.maps.DirectionsRenderer({
      suppressMarkers: true,
      preserveViewport: true,
      polylineOptions: {
        strokeColor: "#0009FF",
        strokeWeight: 6,
        strokeOpacity: 0.8,
      },
    });

    this.directionsRenderer.setMap(this.googleMap);

    if (this.origin.lat) {
      this.drawRoute();
    }
  }

  //==========================
  // Package Details
  //==========================

  getPackageById() {
    this.spinner.show();

    this.packageTrackservice
      .getPackageById({
        id: this.id,
      })
      .subscribe(
        (result: any) => {
          this.spinner.hide();

          if (result.statusCode != 200) {
            this.toastr.error(result.message);
            return;
          }

          const packageData = result.data;

          this.packageId = packageData.packageID;

          this.status = packageData.status;

          this.origin = {
            lat: Number(packageData.fromLatitude),
            lng: Number(packageData.fromLongitude),
          };

          this.destination = {
            lat: Number(packageData.toLatitude),
            lng: Number(packageData.toLongitude),
          };

          this.mapCenter = {
            ...this.origin,
          };

          if (this.googleMap) {
            this.drawRoute();
          }

          this.socketData();
        },
        () => {
          this.spinner.hide();
        },
      );
  }

  //==========================
  // Socket
  //==========================

  socketData() {
    this.socketSubscription = this.websocketService
      .onDriverLocationUpdate()
      .subscribe((data: any) => {
        if (data.packageId != this.packageId) {
          return;
        }

        this.driverLocation = {
          lat: Number(data.lat),
          lng: Number(data.lng),
        };

        // redraw only if moved more than ~40 meters

        if (this.status == 1 || this.status == 2) {
          if (
            this.distanceBetween(
              this.lastRouteOrigin.lat,
              this.lastRouteOrigin.lng,
              this.driverLocation.lat,
              this.driverLocation.lng,
            ) > 40
          ) {
            this.lastRouteOrigin = {
              ...this.driverLocation,
            };

            this.drawRoute();
          }
        }
      });
  }

  //==========================
  // Draw Route
  //==========================

  drawRoute() {
    if (!this.googleMap) {
      return;
    }

    let start;
    let end;

    if (this.status == 1) {
      if (!this.driverLocation.lat) return;

      start = this.driverLocation;
      end = this.origin;
    } else if (this.status == 2) {
      if (!this.driverLocation.lat) return;

      start = this.driverLocation;
      end = this.destination;
    } else {
      start = this.origin;
      end = this.destination;
    }

    this.directionsService.route(
      {
        origin: start,
        destination: end,
        travelMode: google.maps.TravelMode.DRIVING,
      },
      (response: any, status: any) => {
        if (status === "OK") {
          this.directionsRenderer.setDirections(response);
        }
      },
    );
  }

  //==========================
  // Distance in meters
  //==========================

  distanceBetween(lat1: number, lon1: number, lat2: number, lon2: number) {
    if (!lat1 || !lon1) {
      return 999999;
    }

    const R = 6371000;

    const dLat = ((lat2 - lat1) * Math.PI) / 180;

    const dLon = ((lon2 - lon1) * Math.PI) / 180;

    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);

    return R * (2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
  }

  back() {
    this.router.navigateByUrl("dashboard/package");
  }

  ngOnDestroy() {
    if (this.socketSubscription) {
      this.socketSubscription.unsubscribe();
    }
  }
}
